import {
  BLOCK_SIZE_RANGE,
  FILL_COLORS,
  PEN_WIDTH_RANGE,
  type Coverage,
  type MaskStyle,
  type RegionEffect,
  type ShapeKind,
  type ToolMode,
} from "../editor/types";
import { ColorPicker, Popover, Segmented, SliderPopover, type SegmentOption } from "./controls";
import { Icon } from "./Icon";

const MASK_STYLES: ReadonlyArray<SegmentOption<MaskStyle>> = [
  { value: "mosaic", label: "モザイク" },
  { value: "blur", label: "ぼかし" },
  { value: "fill", label: "塗り" },
];

const SHAPES: ReadonlyArray<SegmentOption<ShapeKind>> = [
  { value: "rect", label: "四角", icon: "rectangle", title: "四角 (R)" },
  { value: "ellipse", label: "円", icon: "ellipse", title: "円 (O)" },
  { value: "triangle", label: "三角", icon: "triangle" },
];

const COVERAGES: ReadonlyArray<SegmentOption<Coverage>> = [
  { value: "inside", label: "描いた範囲", title: "描いた範囲だけに効果をかけます" },
  { value: "outside", label: "範囲の外側", title: "描いた範囲以外に効果をかけます" },
  { value: "all", label: "画像全体", title: "画像全体に効果をかけます" },
];

export type EffectKey = keyof RegionEffect;

interface EffectControlsProps {
  effect: RegionEffect;
  mixed: EffectKey[];
  disabled?: boolean;
  onChange: (patch: Partial<RegionEffect>) => void;
  onInteractionStart: () => void;
  onInteractionEnd: () => void;
}

function EffectControls({
  effect,
  mixed,
  disabled,
  onChange,
  onInteractionStart,
  onInteractionEnd,
}: EffectControlsProps) {
  return (
    <>
      <Segmented
        value={mixed.includes("maskStyle") ? ("" as MaskStyle) : effect.maskStyle}
        options={MASK_STYLES}
        label="効果の種類"
        disabled={disabled}
        onChange={(maskStyle) => onChange({ maskStyle })}
      />
      {effect.maskStyle === "fill" ? (
        <Popover
          label="塗りの色"
          disabled={disabled}
          trigger={
            <>
              <span
                className="color-chip"
                style={{ background: mixed.includes("fillColor") ? undefined : effect.fillColor }}
              />
              <Icon name="chevron-down" size={14} />
            </>
          }
        >
          <ColorPicker
            value={effect.fillColor}
            colors={FILL_COLORS}
            label="塗りの色"
            onChange={(fillColor) => onChange({ fillColor })}
          />
        </Popover>
      ) : (
        <SliderPopover
          label={effect.maskStyle === "blur" ? "ぼかしの強さ" : "モザイクの大きさ"}
          shortLabel={effect.maskStyle === "blur" ? "強さ" : "大きさ"}
          value={effect.blockSize}
          mixed={mixed.includes("blockSize")}
          min={BLOCK_SIZE_RANGE.min}
          max={BLOCK_SIZE_RANGE.max}
          step={1}
          unit="px"
          disabled={disabled}
          onInteractionStart={onInteractionStart}
          onInteractionEnd={onInteractionEnd}
          onChange={(blockSize) => onChange({ blockSize })}
        />
      )}
    </>
  );
}

export interface ContextBarProps {
  tool: ToolMode;
  shapeKind: ShapeKind;
  penWidth: number;
  /** Effect for new ranges, or for the selection when one exists. */
  effect: RegionEffect;
  mixed: EffectKey[];
  coverage: Coverage;
  coverageEffect: RegionEffect;
  selectionCount: number;
  /** Brush width shared by the selection when every selected range is a pen stroke. */
  selectionPenWidth: number | null;
  imageCount: number;
  polygonVertices: number;
  disabled?: boolean;
  onShapeKindChange: (kind: ShapeKind) => void;
  onPenWidthChange: (width: number) => void;
  onEffectChange: (patch: Partial<RegionEffect>) => void;
  onCoverageChange: (coverage: Coverage) => void;
  onCoverageEffectChange: (patch: Partial<RegionEffect>) => void;
  onApplyToAll: () => void;
  onInteractionStart: () => void;
  onInteractionEnd: () => void;
  onBrushPreview: (visible: boolean) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onPolygonFinish: () => void;
  onPolygonUndo: () => void;
  onPolygonCancel: () => void;
}

/**
 * The single place where properties are edited, floating over the canvas
 * like Canva's toolbar. Its content follows the tool and the selection.
 */
export function ContextBar(props: ContextBarProps) {
  const {
    tool,
    shapeKind,
    penWidth,
    effect,
    mixed,
    coverage,
    coverageEffect,
    selectionCount,
    selectionPenWidth,
    imageCount,
    polygonVertices,
    disabled,
  } = props;

  const effectControls = (
    <EffectControls
      effect={effect}
      mixed={mixed}
      disabled={disabled}
      onChange={props.onEffectChange}
      onInteractionStart={props.onInteractionStart}
      onInteractionEnd={props.onInteractionEnd}
    />
  );
  // In "outside" mode the drawn ranges are only holes; their effect is unused.
  const rangeEffect =
    coverage === "outside" ? (
      <span className="context-note">
        <Icon name="info" size={15} />
        範囲の外側に効果がかかります
      </span>
    ) : (
      effectControls
    );
  const brushWidth = (label: string, value: number) => (
    <SliderPopover
      label={label}
      shortLabel="太さ"
      value={Math.round(value)}
      min={PEN_WIDTH_RANGE.min}
      max={PEN_WIDTH_RANGE.max}
      step={1}
      unit="px"
      disabled={disabled}
      onInteractionStart={() => {
        props.onBrushPreview(true);
        props.onInteractionStart();
      }}
      onInteractionEnd={() => {
        props.onBrushPreview(false);
        props.onInteractionEnd();
      }}
      onChange={props.onPenWidthChange}
    />
  );
  const divider = <span className="context-divider" aria-hidden="true" />;

  let content;
  if (tool === "select" && selectionCount > 0) {
    content = (
      <>
        <span className="context-title">{selectionCount}件の範囲</span>
        {divider}
        {rangeEffect}
        {selectionPenWidth !== null && (
          <>
            {divider}
            {brushWidth("選択中のペンの太さ", selectionPenWidth)}
          </>
        )}
        {divider}
        <button
          className="icon-button"
          type="button"
          aria-label="範囲を複製"
          title="複製 (Ctrl/Cmd+D)"
          disabled={disabled}
          onClick={props.onDuplicate}
        >
          <Icon name="copy" size={18} />
        </button>
        <button
          className="icon-button"
          type="button"
          aria-label="範囲を削除"
          title="削除 (Delete)"
          disabled={disabled}
          onClick={props.onDelete}
        >
          <Icon name="trash" size={18} />
        </button>
      </>
    );
  } else if (tool === "select") {
    content = (
      <>
        <span className="context-title">効果をかける場所</span>
        <Segmented
          value={coverage}
          options={COVERAGES}
          label="効果をかける場所"
          disabled={disabled}
          onChange={props.onCoverageChange}
        />
        {coverage !== "inside" && (
          <>
            {divider}
            <EffectControls
              effect={coverageEffect}
              mixed={[]}
              disabled={disabled}
              onChange={props.onCoverageEffectChange}
              onInteractionStart={props.onInteractionStart}
              onInteractionEnd={props.onInteractionEnd}
            />
          </>
        )}
        {imageCount > 1 && (
          <>
            {divider}
            <button
              className="text-button"
              type="button"
              title="この設定をほかの画像にも適用"
              disabled={disabled}
              onClick={props.onApplyToAll}
            >
              全画像に適用
            </button>
          </>
        )}
      </>
    );
  } else if (tool === "pen") {
    content = (
      <>
        {brushWidth("ペンの太さ", penWidth)}
        {divider}
        {rangeEffect}
      </>
    );
  } else if (tool === "shape") {
    content = (
      <>
        <Segmented
          value={shapeKind}
          options={SHAPES}
          label="図形"
          disabled={disabled}
          onChange={props.onShapeKindChange}
        />
        {divider}
        {rangeEffect}
      </>
    );
  } else if (tool === "polygon") {
    content =
      polygonVertices > 0 ? (
        <>
          <span className="context-hint">
            {polygonVertices < 3
              ? "クリックで頂点を追加"
              : "始点をクリック、またはダブルクリックで完成"}
          </span>
          {divider}
          <button
            className="text-button is-primary"
            type="button"
            disabled={polygonVertices < 3}
            onClick={props.onPolygonFinish}
          >
            完成 <kbd>Enter</kbd>
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="最後の頂点を取り消す"
            title="最後の頂点を取り消す (Backspace)"
            onClick={props.onPolygonUndo}
          >
            <Icon name="undo" size={17} />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="多角形をやめる"
            title="やめる (Esc)"
            onClick={props.onPolygonCancel}
          >
            <Icon name="close" size={17} />
          </button>
        </>
      ) : (
        <>
          <span className="context-hint">クリックで頂点を置く／ドラッグで自由に囲む</span>
          {divider}
          {rangeEffect}
        </>
      );
  } else if (tool === "erase") {
    content = (
      <>
        {brushWidth("消しゴムの太さ", penWidth)}
        {divider}
        <span className="context-hint">なぞった部分の効果を取り除きます</span>
      </>
    );
  } else {
    return null;
  }

  return (
    <div className="context-bar" role="toolbar" aria-label="ツールの設定">
      {content}
    </div>
  );
}
